/**
 * IdentityAgentsPageV2 — Phase 6G (P0-IDENT).
 *
 * Agent-scope list view. Lists every agent the user can see decorated
 * with its identity-plane state: SPIFFE ID, trust band, SVID lifecycle
 * status, and whether the agent carries any per-agent overrides.
 *
 * Reached from the landing page's Agent scope card (was "Coming in 6G"
 * pre-6G; enabled in 6G-7) and as a sibling of /identity/organization
 * and /identity/tenants.
 *
 * Composition
 *   1. Fleet KPI strip — total / expiring / expired / probation
 *   2. Filter strip — search + trust band + SVID-status quick filters
 *   3. Agents table — one row per agent, click → /identity/agents/:id
 *
 * Data
 *   Real fetch against GET /api/v1/identity/agents. Returns rows
 *   pulled from AgentRepository, decorated server-side with the
 *   computed SVID status. Loading + empty + error states all
 *   handled — no UI assumptions about success.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Filter, Search, AlertTriangle, Clock, ShieldCheck, Plus,
  ChevronRight, Fingerprint, X as XIcon,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { listIdentityAgents } from '../services/api';

/* ────────────────────────────────────────────────────────────────
   SVID status visual mapping. Static maps for Tailwind JIT safety
   (see comment in IdentityLandingPageV2 about dynamic class strings).
   ──────────────────────────────────────────────────────────────── */

const SVID_STATUS_TONE = {
  active:          'success',
  expiring:        'warning',
  expired:         'danger',
  not_provisioned: 'muted',
};
const SVID_STATUS_LABEL = {
  active:          'Active',
  expiring:        'Expiring',
  expired:         'Expired',
  not_provisioned: 'Not provisioned',
};

const TRUST_BAND_TONE = {
  PROBATION:  'warning',
  SUPERVISED: 'cyan',
  ACTIVE:     'success',
  AUTONOMOUS: 'trust',
  /* Older agents may have null/missing band — render as muted. */
  null:       'muted',
};

/* Format expiry timestamp as "in 23 min" / "12h ago" / etc.
 * Returns null when the agent has no expiry yet (not_provisioned). */
function formatRelative(iso) {
  if (!iso) return null;
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMin = Math.round((t - now) / 60000);
  if (diffMin === 0) return 'now';
  if (Math.abs(diffMin) < 60) return diffMin > 0 ? `in ${diffMin} min` : `${-diffMin} min ago`;
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) return diffH > 0 ? `in ${diffH}h` : `${-diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return diffD > 0 ? `in ${diffD}d` : `${-diffD}d ago`;
}

/* Truncate a SPIFFE ID for table display. Keeps trust domain + last
 * segment so the row stays scannable but unique. */
function truncateSpiffeId(spiffeId) {
  if (!spiffeId) return '—';
  const m = /^spiffe:\/\/([^/]+)\/(.+)$/.exec(spiffeId);
  if (!m) return spiffeId;
  const [, td, path] = m;
  const segs = path.split('/');
  if (segs.length <= 2) return spiffeId;
  const last = segs[segs.length - 1];
  return `spiffe://${td}/…/${last}`;
}

/* ────────────────────────────────────────────────────────────────
   IdentityAgentsPageV2 — top-level export
   ──────────────────────────────────────────────────────────────── */

export default function IdentityAgentsPageV2() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state — purely client-side over the list returned from the
  // API. The dataset is bounded (an org with thousands of agents is
  // unusual on the identity surface, since each agent corresponds to a
  // managed SVID). When that changes, push these filters to the API.
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);  // 'expiring' | 'expired' | null
  const [overrideOnly, setOverrideOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // apiFetch ALWAYS resolves — it wraps success as { ok: true, data }
    // and 4xx/5xx as { ok: false, error }. The .catch is dead code in
    // the normal contract but kept as a safety net for unexpected
    // throws (e.g. abort errors).
    listIdentityAgents()
      .then((res) => {
        if (cancelled) return;
        if (res?.ok === false) {
          setError(res.error || 'Identity API error');
        } else {
          setData(res?.data || null);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || String(err));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  /* Filtered rows. Memoised because the page re-renders on every
   * keystroke and we don't want to re-filter a stable list mid-render. */
  const visibleAgents = useMemo(() => {
    if (!data?.agents) return [];
    const q = query.trim().toLowerCase();
    return data.agents.filter((a) => {
      if (statusFilter && a.svidStatus !== statusFilter) return false;
      if (overrideOnly && !a.hasOverride) return false;
      if (q) {
        const haystack = [a.name, a.spiffeId, a.trustBand, a.archetype]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [data, query, statusFilter, overrideOnly]);

  const clearFilters = () => {
    setQuery('');
    setStatusFilter(null);
    setOverrideOnly(false);
  };

  const hasActiveFilters = query || statusFilter || overrideOnly;

  return (
    <V2PageShell
      title="Agents"
      subtitle={
        data
          ? `${data.total} agents · ${data.expiringSoon} expiring soon · ${data.expired} expired · ${data.probationCount} on probation`
          : 'Identity-plane state for every agent in scope'
      }
      backTo="/identity"
      backLabel="Back to Identity"
      tags={
        <>
          <Pill tone="cyan"   size="sm">Agent</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
        </>
      }
      actions={
        <Button
          variant="primary"
          size="sm"
          iconLeft={<Plus size={14} />}
          onClick={() => navigate('/agents')}
        >
          New agent
        </Button>
      }
    >
      {/* Fleet KPI strip ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile label="Agents"        value={data?.total ?? '—'}          icon={<Bot           size={14} />} tone="action"  />
        <KpiTile label="Expiring soon" value={data?.expiringSoon ?? '—'}   icon={<Clock         size={14} />} tone="warning" />
        <KpiTile label="Expired"       value={data?.expired ?? '—'}        icon={<AlertTriangle size={14} />} tone="danger"  />
        <KpiTile label="Probation"     value={data?.probationCount ?? '—'} icon={<ShieldCheck   size={14} />} tone="trust"   />
      </div>

      {/* Filter strip ──────────────────────────────────────────── */}
      <Card padding="sm" className="mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search size={14} className="text-v2-ink-muted" aria-hidden="true" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, SPIFFE ID, trust band…"
              className="flex-1 bg-transparent border-0 outline-none text-[13px] text-v2-ink placeholder:text-v2-ink-muted"
              aria-label="Search agents"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <FilterPill
              label="Expiring soon"
              active={statusFilter === 'expiring'}
              onClick={() => setStatusFilter(statusFilter === 'expiring' ? null : 'expiring')}
            />
            <FilterPill
              label="Expired"
              active={statusFilter === 'expired'}
              onClick={() => setStatusFilter(statusFilter === 'expired' ? null : 'expired')}
            />
            <FilterPill
              label="With override"
              active={overrideOnly}
              onClick={() => setOverrideOnly((v) => !v)}
            />
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="xs" iconLeft={<XIcon size={11} />} onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Loading / Error / Empty / Table  ──────────────────────── */}
      {loading && <AgentsTableSkeleton />}

      {!loading && error && (
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title="Could not load agents"
            description={`Identity API returned an error: ${error}`}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && visibleAgents.length === 0 && (
        <Card>
          <EmptyState
            icon={<Fingerprint />}
            title={hasActiveFilters ? 'No agents match these filters' : 'No agents yet'}
            description={
              hasActiveFilters
                ? 'Clear the filters above to see all agents in scope.'
                : 'Create your first agentlet, and its SVID identity will appear here automatically.'
            }
            primaryAction={
              hasActiveFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button variant="primary" size="sm" iconLeft={<Plus size={14} />} onClick={() => navigate('/agents')}>
                  Create agent
                </Button>
              )
            }
          />
        </Card>
      )}

      {!loading && !error && visibleAgents.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          {/* Column headers */}
          <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1.6fr_0.9fr_0.5fr] gap-2 px-4 py-2.5 bg-v2-muted text-[10px] uppercase tracking-wide text-v2-ink-muted font-semibold">
            <div>Agent</div>
            <div>Archetype</div>
            <div>Trust band</div>
            <div>SPIFFE ID</div>
            <div>SVID</div>
            <div></div>
          </div>

          {visibleAgents.map((a) => (
            <AgentRow key={a.id} agent={a} onClick={() => navigate(`/identity/agents/${a.id}`)} />
          ))}
        </Card>
      )}
    </V2PageShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Helpers — KpiTile, FilterPill, AgentRow, TableSkeleton.
   ──────────────────────────────────────────────────────────────── */

function KpiTile({ label, value, icon, tone }) {
  // Static tone-to-class map keeps Tailwind JIT happy. Mirrors the
  // landing-page KPI strip tones.
  const BG = {
    action:  'bg-v2-action-soft  text-v2-action',
    success: 'bg-v2-success-soft text-v2-success',
    warning: 'bg-v2-warning-soft text-v2-warning',
    danger:  'bg-v2-danger-soft  text-v2-danger',
    trust:   'bg-v2-trust-soft   text-v2-trust',
  };
  return (
    <Card padding="sm">
      <div className="flex items-start justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wide text-v2-ink-muted font-semibold">{label}</div>
        <div className={'w-6 h-6 rounded-md flex items-center justify-center ' + (BG[tone] || BG.action)}>
          {icon}
        </div>
      </div>
      <div className="text-[22px] font-semibold text-v2-ink leading-tight">{value}</div>
    </Card>
  );
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'v2-focus-ring px-3 h-7 rounded-v2-pill text-[12px] font-medium transition-colors ' +
        (active
          ? 'bg-v2-action text-white border border-v2-action'
          : 'bg-transparent text-v2-ink-secondary border border-v2-border-subtle hover:border-v2-border hover:bg-v2-muted')
      }
    >
      {label}
    </button>
  );
}

function AgentRow({ agent, onClick }) {
  const statusTone = SVID_STATUS_TONE[agent.svidStatus] || 'muted';
  const statusLabel = SVID_STATUS_LABEL[agent.svidStatus] || agent.svidStatus;
  const trustBandKey = agent.trustBand || 'null';
  const trustBandTone = TRUST_BAND_TONE[trustBandKey] || 'muted';
  const relExpiry = formatRelative(agent.svidExpiresAt);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1.6fr_0.9fr_0.5fr] gap-2 px-4 py-3 border-t border-v2-border-subtle items-center cursor-pointer hover:bg-v2-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-v2-ink flex items-center gap-1.5 truncate">
          {agent.name || <span className="text-v2-ink-muted">unnamed</span>}
          {agent.hasOverride && <Pill tone="trust" size="sm">override</Pill>}
        </div>
        <div className="text-[11px] text-v2-ink-muted truncate">
          {agent.agentClassification || 'INTERNAL'}
        </div>
      </div>

      <div><Pill tone="muted" size="sm">{agent.archetype || 'Let'}</Pill></div>

      <div><Pill tone={trustBandTone} size="sm">{agent.trustBand || '—'}</Pill></div>

      <div className="font-mono-v2 text-[11.5px] text-v2-ink-secondary truncate" title={agent.spiffeId}>
        {truncateSpiffeId(agent.spiffeId)}
      </div>

      <div className="flex flex-col">
        <Pill tone={statusTone} size="sm">{statusLabel}</Pill>
        {relExpiry && (
          <div className="text-[10.5px] text-v2-ink-muted mt-0.5">{relExpiry}</div>
        )}
      </div>

      <div className="text-right">
        <ChevronRight size={14} className="text-v2-ink-muted inline" aria-hidden="true" />
      </div>
    </div>
  );
}

function AgentsTableSkeleton() {
  return (
    <Card padding="none" className="overflow-hidden">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1.6fr_0.9fr_0.5fr] gap-2 px-4 py-3 border-t border-v2-border-subtle items-center first:border-t-0">
          <div><Skeleton width="80%" height={14} /></div>
          <div><Skeleton width="50%" height={14} /></div>
          <div><Skeleton width="60%" height={14} /></div>
          <div><Skeleton width="85%" height={14} /></div>
          <div><Skeleton width="55%" height={14} /></div>
          <div></div>
        </div>
      ))}
    </Card>
  );
}
